<template>
  <div class="empty-dimension-repair-tab p-6 max-w-6xl">
    <div class="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
      <h2 class="text-xl font-semibold text-amber-950 mb-2">
        <i class="pi pi-exclamation-triangle mr-2"></i>
        Empty dimension repair
      </h2>
      <p class="text-sm text-amber-900/90 leading-relaxed">
        Finds external evaluations where empty dimensions were stored as phantom scores (usually 1).
        Run a dry run, review by series, then apply one assessment at a time.
      </p>
    </div>

    <div class="flex flex-wrap gap-3 mb-6">
      <Button
        label="Run dry run"
        icon="pi pi-search"
        :loading="loadingDryRun"
        class="p-button-info"
        @click="runDryRun"
      />
      <Button
        v-if="pendingRows.length"
        label="Apply all remaining"
        icon="pi pi-check"
        :loading="loadingApplyAll"
        class="p-button-success"
        @click="runApplyAll"
      />
      <Button
        v-if="dryRunReport || repairedCount > 0"
        label="Clear results"
        icon="pi pi-times"
        class="p-button-secondary"
        :disabled="loadingDryRun || loadingApplyAll || applyingRowIds.size > 0"
        @click="clearResults"
      />
    </div>

    <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800 text-sm">
      {{ errorMessage }}
    </div>

    <div v-if="dryRunReport" class="space-y-6">
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Affected" :value="dryRunReport.assessmentsAffected ?? 0" />
        <StatCard label="Dimensions" :value="dryRunReport.dimensionsAffected ?? 0" />
        <StatCard label="Series" :value="dryRunReport.uniqueSeriesAffected ?? 0" />
        <StatCard label="Repaired" :value="repairedCount" accent="green" />
      </div>

      <div v-if="!dryRunReport.rows?.length" class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-600 text-sm">
        No phantom empty dimension scores found on tenant
        <span class="font-mono">{{ dryRunReport.tenantSlug }}</span>.
      </div>

      <div v-else class="space-y-5">
        <div
          v-for="group in seriesGroups"
          :key="group.seriesId"
          class="rounded-xl border border-slate-300 bg-slate-50 overflow-hidden shadow-sm"
        >
          <div class="px-4 py-3 bg-white border-b border-slate-200 flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Series</div>
              <div v-if="group.seriesLabel" class="text-base font-semibold text-slate-900 leading-tight mb-2">
                {{ group.seriesLabel }}
              </div>
              <CopyLine label="Series ID" :value="group.seriesId" mono />
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span class="rounded-full bg-slate-100 border border-slate-200 text-slate-700 px-3 py-1 text-xs font-medium">
                {{ group.rows.length }} assessment{{ group.rows.length === 1 ? '' : 's' }}
              </span>
            </div>
          </div>

          <div class="divide-y divide-slate-200">
            <article
              v-for="row in group.rows"
              :key="row.assessmentId"
              class="p-4 bg-white"
              :class="rowStatusClass(row)"
            >
              <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div class="flex items-center gap-2 flex-wrap">
                  <StatusBadge :status="rowStatus(row)" />
                  <span class="text-xs text-slate-500">
                    Schema: <span class="font-medium text-slate-700">{{ row.schemaType }}</span>
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <Button
                    v-if="!isRowRepaired(row.assessmentId)"
                    label="Apply repair"
                    icon="pi pi-check"
                    class="p-button-success p-button-sm"
                    :loading="applyingRowIds.has(row.assessmentId)"
                    :disabled="loadingApplyAll"
                    @click="runApplyOne(row)"
                  />
                  <span
                    v-else
                    class="inline-flex items-center gap-1.5 rounded-full bg-green-100 text-green-800 px-3 py-1.5 text-xs font-semibold"
                  >
                    <i class="pi pi-check-circle"></i>
                    Repaired
                  </span>
                </div>
              </div>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                <UserRepairCell role-label="Ratee" :user="row.ratee" />
                <UserRepairCell role-label="Rater" :user="row.rater" />
              </div>

              <div class="rounded-lg border border-amber-200 bg-amber-50/70 p-3 mb-3">
                <div class="text-[10px] font-semibold uppercase tracking-wide text-amber-800 mb-2">
                  Phantom dimensions → null
                </div>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="change in row.dimensionChanges"
                    :key="change.dimension"
                    class="inline-flex items-center rounded-md bg-white border border-amber-200 px-2.5 py-1 font-mono text-xs text-amber-950"
                  >
                    {{ change.dimension }}
                    <span class="mx-1.5 text-amber-400">|</span>
                    <span class="line-through text-red-600">{{ change.current }}</span>
                    <i class="pi pi-arrow-right mx-1.5 text-[10px] text-amber-500"></i>
                    <span class="text-slate-500">null</span>
                  </span>
                </div>
              </div>

              <div class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <CopyLine label="Assessment ID" :value="row.assessmentId" mono />
              </div>

              <p
                v-if="rowErrors[row.assessmentId]"
                class="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              >
                {{ rowErrors[row.assessmentId] }}
              </p>
            </article>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import migrationService from '@/services/migrationService';
import CopyLine from './CopyLine.vue';
import UserRepairCell from './UserRepairCell.vue';

const StatCard = {
  name: 'StatCard',
  props: {
    label: String,
    value: [String, Number],
    accent: { type: String, default: '' },
  },
  template: `
    <div class="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div class="text-xs text-slate-500 mb-1">{{ label }}</div>
      <div class="text-2xl font-bold" :class="accent === 'green' ? 'text-green-700' : 'text-slate-800'">{{ value }}</div>
    </div>
  `,
};

const StatusBadge = {
  name: 'StatusBadge',
  props: { status: String },
  computed: {
    classes() {
      if (this.status === 'repaired') return 'bg-green-100 text-green-800 border-green-200';
      if (this.status === 'error') return 'bg-red-100 text-red-800 border-red-200';
      return 'bg-amber-100 text-amber-900 border-amber-200';
    },
    label() {
      if (this.status === 'repaired') return 'Repaired';
      if (this.status === 'error') return 'Failed';
      return 'Pending';
    },
  },
  template: `
    <span class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold" :class="classes">
      {{ label }}
    </span>
  `,
};

export default {
  name: 'EmptyDimensionRepairTab',
  components: { Button, CopyLine, UserRepairCell, StatCard, StatusBadge },
  data() {
    return {
      loadingDryRun: false,
      loadingApplyAll: false,
      dryRunReport: null,
      errorMessage: null,
      repairedAssessmentIds: [],
      applyingRowIds: new Set(),
      rowErrors: {},
    };
  },
  computed: {
    displayRows() {
      return this.dryRunReport?.rows ?? [];
    },
    pendingRows() {
      return this.displayRows.filter((row) => !this.isRowRepaired(row.assessmentId));
    },
    repairedCount() {
      return this.repairedAssessmentIds.length;
    },
    seriesGroups() {
      const groups = new Map();
      for (const row of this.displayRows) {
        const key = row.seriesId || 'unknown-series';
        if (!groups.has(key)) {
          groups.set(key, {
            seriesId: row.seriesId,
            seriesLabel: row.seriesLabel,
            rows: [],
          });
        }
        groups.get(key).rows.push(row);
      }
      return Array.from(groups.values());
    },
  },
  methods: {
    isRowRepaired(assessmentId) {
      return this.repairedAssessmentIds.includes(assessmentId);
    },
    rowStatus(row) {
      if (this.isRowRepaired(row.assessmentId)) return 'repaired';
      if (this.rowErrors[row.assessmentId]) return 'error';
      return 'pending';
    },
    rowStatusClass(row) {
      if (this.isRowRepaired(row.assessmentId)) return 'bg-green-50/60';
      if (this.rowErrors[row.assessmentId]) return 'bg-red-50/60';
      return '';
    },
    async runDryRun() {
      this.errorMessage = null;
      this.dryRunReport = null;
      this.repairedAssessmentIds = [];
      this.rowErrors = {};
      this.loadingDryRun = true;
      try {
        const res = await migrationService.runEmptyDimensionRepairDryRun();
        this.dryRunReport = res?.data?.data ?? res?.data ?? res;
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingDryRun = false;
      }
    },
    async runApplyOne(row) {
      const label = row.rater?.email || row.rater?.name || row.assessmentId;
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Apply repair for rater ${label}? Empty dimensions will be set to null.`,
      );
      if (!ok) return;

      this.errorMessage = null;
      const nextApplying = new Set(this.applyingRowIds);
      nextApplying.add(row.assessmentId);
      this.applyingRowIds = nextApplying;
      const { [row.assessmentId]: _removed, ...restErrors } = this.rowErrors;
      this.rowErrors = restErrors;

      try {
        const res = await migrationService.runEmptyDimensionRepairOne(row.assessmentId);
        const payload = res?.data?.data ?? res?.data ?? res;
        if (payload?.errors > 0) {
          const detail = payload.errorDetails?.[0]?.error || 'Repair failed';
          this.rowErrors = { ...this.rowErrors, [row.assessmentId]: detail };
          return;
        }
        if (!this.repairedAssessmentIds.includes(row.assessmentId)) {
          this.repairedAssessmentIds = [...this.repairedAssessmentIds, row.assessmentId];
        }
      } catch (e) {
        this.rowErrors = {
          ...this.rowErrors,
          [row.assessmentId]: e?.response?.data?.message || e.message || String(e),
        };
      } finally {
        const doneApplying = new Set(this.applyingRowIds);
        doneApplying.delete(row.assessmentId);
        this.applyingRowIds = doneApplying;
      }
    },
    async runApplyAll() {
      if (!this.pendingRows.length) return;
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Apply repair for ${this.pendingRows.length} remaining assessment(s)?`,
      );
      if (!ok) return;

      this.errorMessage = null;
      this.loadingApplyAll = true;
      try {
        const res = await migrationService.runEmptyDimensionRepair();
        const payload = res?.data?.data ?? res?.data ?? res;
        const failed = payload?.errorDetails ?? [];
        const failedIds = new Set(failed.map((item) => item.assessmentId));

        if (failed.length) {
          const errors = { ...this.rowErrors };
          for (const item of failed) {
            errors[item.assessmentId] = item.error;
          }
          this.rowErrors = errors;
        }

        const newlyRepaired = this.pendingRows
          .map((row) => row.assessmentId)
          .filter((id) => !failedIds.has(id) && !this.repairedAssessmentIds.includes(id));

        if (newlyRepaired.length) {
          this.repairedAssessmentIds = [...this.repairedAssessmentIds, ...newlyRepaired];
        }

        if (payload?.errors > 0 && failed.length === 0) {
          this.errorMessage = `Repair completed with ${payload.errors} error(s).`;
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingApplyAll = false;
      }
    },
    clearResults() {
      this.dryRunReport = null;
      this.repairedAssessmentIds = [];
      this.rowErrors = {};
      this.errorMessage = null;
      this.applyingRowIds = new Set();
    },
  },
};
</script>

<style scoped>
.empty-dimension-repair-tab :deep(.repair-user-block) {
  min-height: 7.5rem;
}
</style>
